import { Dialog, DialogContent, DialogTitle, CircularProgress, Box } from '@mui/material'
import { t } from 'i18next'
import { useState, useCallback } from 'react'

export interface GeolocationResult {
    latitude: number
    longitude: number
}

/**
 * Hook that shows a loading dialog and attempts to acquire user's current location.
 * Resolves with coordinates on success, null on error.
 * Includes 20 second timeout.
 */
export const useGeolocationDialog = () => {
    const [open, setOpen] = useState(false)

    const acquireLocation = useCallback(
        (onSuccess: (location: GeolocationResult) => void, onError?: () => void) => {
            setOpen(true)

            let timeoutId: NodeJS.Timeout | null = null

            const cleanup = (shouldClearTimeout: boolean = true) => {
                setOpen(false)
                if (shouldClearTimeout && timeoutId) clearTimeout(timeoutId)
            }

            timeoutId = setTimeout(() => {
                cleanup(false)
                alert(t('Location acquisition timed out. Please try again.'))
                onError?.()
            }, 20000)

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    cleanup()
                    onSuccess({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    })
                },
                (error) => {
                    cleanup()
                    if (error.code === error.PERMISSION_DENIED) {
                        alert(t('Location permission denied. Please enable location services in your browser settings.'))
                    }
                    else {
                        alert(t('Error acquiring location: ') + error.message)
                    }
                    onError?.()
                },
                { enableHighAccuracy: true },
            )
        },
        [],
    )

    const LoadingDialog = () => (
        <Dialog open={open}>
            <DialogTitle>{t('Acquiring location...')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress />
                </Box>
            </DialogContent>
        </Dialog>
    )

    return {
        open,
        acquireLocation,
        LoadingDialog,
    }
}
